import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { RiskEditSheet } from '../../../-components/RiskEditSheet';
import { RisksTable } from '../../../-components/RisksTable';
import { adminRisksQueryOptions } from '../../../-hooks/useAdminRisks';
import { useRisksTab } from '../../../-hooks/useRisksTab';

const searchSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  search: z.string().optional(),
});

export const Route = createFileRoute(
  '/management/risk-management/(pages)/(list)/risks/',
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on failure — see the risk-gross route.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminRisksQueryOptions(deps))
      .catch(() => undefined),
  component: RisksTabPage,
});

function RisksTabPage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementRiskManagement',
  });
  const tab = useRisksTab(t);

  return (
    <PageContainer>
      <RisksTable
        rows={tab.list.rows}
        total={tab.list.total}
        isLoading={tab.list.isLoading}
        filters={tab.list.filters}
        pagination={tab.list.pagination}
        onFiltersChange={tab.list.onFiltersChange}
        onPaginationChange={tab.list.onPaginationChange}
        onCreate={tab.openCreate}
        onEdit={tab.openEdit}
      />
      <RiskEditSheet
        open={tab.sheet.open}
        risk={tab.sheet.risk}
        draft={tab.draft}
        isSaving={tab.isSaving}
        onDraftChange={tab.setDraft}
        onOpenChange={(open: boolean) => !open && tab.close()}
        onSubmit={() => void tab.submit()}
      />
    </PageContainer>
  );
}
